// Product settings > AI Presence tab: the competitors to track and prompts to
// always benchmark (saved on their own endpoint, PATCH .../ai-presence/
// settings, like the Consumer Voice hints), a Run AI Presence button, the
// product's fixed benchmark prompt set, and its analysis history with the
// score-over-time chart.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import axiosInstant from '../../../../api';
import {
  productAiPresenceEndpoint,
  productAiPresenceItemEndpoint,
  productAiPresencePromptsEndpoint,
  productAiPresenceSettingsEndpoint,
} from '../../../../api/endpoints';
import { AiPresenceHistorySection } from '../../../../components/AiPresence/AiPresenceHistorySection';
import { AIP_INTENT_LABELS, AiPresenceAudit } from '../../../../types/aiPresence';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import { useUser } from '../../../../hooks/useUser';
import { isInternal } from '../../../../utils';

interface Props {
  desireid: number;
  productTitle?: string | null;
  // The product's own (manufacturer) brand — the run is stamped with it.
  brandId: number | null;
}

interface PromptSet {
  id: number;
  version: number;
  created_at: string;
  prompts: Array<{ key: string; text: string; intent: string; branded: boolean; est_monthly_searches: number | null }>;
  competitors: Array<{ name: string }>;
}

const inputClass =
  'w-full px-3 py-2.5 rounded-lg border border-border-token bg-surface text-[13px] text-text-1 focus:outline-none focus:border-accent-text';

export function ProductAiPresencePanel({ desireid, productTitle, brandId }: Props) {
  const { role } = useUser();
  const navigate = useNavigate();
  const { basePath, inWorkspace } = useNetworkScope();
  const [competitors, setCompetitors] = useState('');
  const [savedCompetitors, setSavedCompetitors] = useState('');
  const [prompts, setPrompts] = useState('');
  const [savedPrompts, setSavedPrompts] = useState('');
  const [brand, setBrand] = useState<{ name: string | null; competitors: string | null; prompts: string | null }>({
    name: null,
    competitors: null,
    prompts: null,
  });
  const [set, setSet] = useState<PromptSet | null>(null);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [regenerate, setRegenerate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(`${productAiPresenceSettingsEndpoint(desireid)}${brandId ? `?brandId=${brandId}` : ''}`)
      .then((res) => {
        if (cancelled || !res.data) return;
        setCompetitors(res.data.aip_competitors || '');
        setSavedCompetitors(res.data.aip_competitors || '');
        setPrompts(res.data.aip_prompts || '');
        setSavedPrompts(res.data.aip_prompts || '');
        setBrand({
          name: res.data.brand_name || null,
          competitors: res.data.brand_aip_competitors || null,
          prompts: res.data.brand_aip_prompts || null,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [desireid, brandId]);

  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(productAiPresencePromptsEndpoint(desireid))
      .then((res) => {
        if (!cancelled) setSet((res.data as PromptSet) || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [desireid, refreshKey, running]);

  const dirty = competitors.trim() !== savedCompetitors.trim() || prompts.trim() !== savedPrompts.trim();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstant.patch(productAiPresenceSettingsEndpoint(desireid), {
        aip_competitors: competitors,
        aip_prompts: prompts,
      });
      setCompetitors((data && data.aip_competitors) || '');
      setSavedCompetitors((data && data.aip_competitors) || '');
      setPrompts((data && data.aip_prompts) || '');
      setSavedPrompts((data && data.aip_prompts) || '');
      toast.success('AI Presence hints saved.');
    } catch {
      toast.error("Couldn't save the hints. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    if (!brandId) {
      toast.error("Set the product's brand in General first — the analysis is stamped with it.");
      return;
    }
    setStarting(true);
    try {
      if (dirty) await save();
      await axiosInstant.post(productAiPresenceEndpoint(desireid), { brandId, regenerate_prompts: regenerate || undefined });
      toast.success('AI Presence analysis started — it takes a minute or two.');
      setRunning(true);
      setRegenerate(false);
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) toast('An AI Presence analysis is already running for this product.');
      else if (status === 429) toast.error("You've reached the hourly limit for AI Presence analyses.");
      else toast.error("Couldn't start the AI Presence analysis. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  // Same hand-off as the Consumer Voice tab: the wizard reads ?aip=<id>.
  const createStrategy = (audit: AiPresenceAudit) => {
    const params = new URLSearchParams({ desireid: String(desireid), aip: String(audit.id) });
    if (productTitle) params.set('title', productTitle);
    const path = inWorkspace || !isInternal(role) ? `${basePath}/opportunities/strategies/new` : '/proposal';
    navigate(`${path}?${params.toString()}`);
  };

  const listEndpoint = `${productAiPresenceEndpoint(desireid)}${brandId ? `?brandId=${brandId}` : ''}`;
  let runLabel = 'Run AI Presence';
  if (starting) runLabel = 'Starting…';
  else if (running) runLabel = 'Analysis running…';

  return (
    <div className="flex flex-col gap-5 pt-4">
      <div>
        <label htmlFor="aip-competitors" className="block text-[13px] font-semibold text-text-1 mb-1.5">
          Competitors to track
        </label>
        <input
          id="aip-competitors"
          type="text"
          value={competitors}
          onChange={(e) => setCompetitors(e.target.value)}
          placeholder="e.g. JBL Charge 6, Sonos Move 2"
          className={inputClass}
        />
        <p className="text-[12px] text-muted mt-1.5">Comma-separated (up to 10). Seeds the comparison prompts.</p>
        {brand.competitors ? (
          <p className="text-[12px] text-text-2 mt-1.5">
            Also inherited from {brand.name || 'the brand'}: <span className="font-semibold">{brand.competitors}</span>
          </p>
        ) : null}
        <label htmlFor="aip-prompts" className="block text-[13px] font-semibold text-text-1 mb-1.5 mt-4">
          Prompts to always include
        </label>
        <textarea
          id="aip-prompts"
          rows={3}
          value={prompts}
          onChange={(e) => setPrompts(e.target.value)}
          placeholder="One per line, e.g. What is the best waterproof speaker for kayaking?"
          className={inputClass}
        />
        <p className="text-[12px] text-muted mt-1.5">
          One per line (up to 10). Added when the benchmark prompt set is built — the first run, or a new benchmark version.
        </p>
        {brand.prompts ? (
          <p className="text-[12px] text-text-2 mt-1.5">
            Also inherited from {brand.name || 'the brand'}: <span className="font-semibold">{brand.prompts.split('\n').join(' · ')}</span>
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 mt-2.5">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="text-[12px] font-semibold text-white bg-primary rounded-md px-3 py-1.5 cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save hints'}
          </button>
          <button
            type="button"
            onClick={run}
            disabled={starting || running}
            title={brandId ? 'Ask Claude, Gemini and OpenAI the shopping prompts and score the answers' : 'Set the product brand first'}
            className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-3 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {runLabel}
          </button>
          {isInternal(role) && set ? (
            <label className="flex items-center gap-1.5 text-[12px] text-text-2 cursor-pointer">
              <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
              New benchmark prompt set (restarts the trend)
            </label>
          ) : null}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[16px] font-bold text-text-1">Benchmark prompts</h2>
          {set ? (
            <span className="text-[12px] text-muted">
              v{set.version} · {set.prompts.length} prompts
            </span>
          ) : null}
        </div>
        {set ? (
          <ul className="flex flex-col gap-1.5">
            {set.prompts.map((p) => (
              <li key={p.key} className="flex items-baseline gap-2 text-[13px] text-text-1">
                <span className="shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-surface-2 text-text-2">
                  {AIP_INTENT_LABELS[p.intent] || p.intent}
                </span>
                <span className="min-w-0">{p.text}</span>
                {p.est_monthly_searches ? (
                  <span className="shrink-0 text-[11px] text-muted">~{p.est_monthly_searches.toLocaleString('en-US')}/mo</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted">
            The first run builds this product&apos;s fixed set of estimated shopping prompts from search demand, Consumer Voice and the
            product page. Later runs reuse it, so scores are comparable over time.
          </p>
        )}
      </div>

      <AiPresenceHistorySection
        title="AI Presence History"
        listEndpoint={listEndpoint}
        itemEndpoint={(id) => productAiPresenceItemEndpoint(desireid, id, brandId)}
        refreshKey={refreshKey}
        showChart
        onRowsChange={(rows) => setRunning(rows.some((r) => r.status === 'pending'))}
        onCreateStrategy={createStrategy}
      />
    </div>
  );
}
